<template>
  <section class="sections-development-banner">
    <div class="__development-banner-wrapper">
      <!-- Image -->
      <div class="__development-banner-image-wrapper" :style="`top: ${parallaxTopPosition}px;`" @click="isGalleryOpen = true;">
        <nuxt-img :src="typeof company?.bannerUrl === 'string' ? company.bannerUrl : ''" class="__development-banner-primary-image __column" format="webp" fit="cover" height="420" width="100%" />
      </div>

      <!-- Go back button -->
      <div class="__development-banner-go-back-button-wrapper">
        <div class="container">
          <ElementsAvatar class="__development-banner-go-back-btn" :icon-src="chevronDownIcon" icon-alt="Parking icon" @click="goBack" />
          <ElementsAvatar :icon-src="galleryIcon" icon-alt="Parking icon" is-clickable @click="isGalleryOpen = true;" />
        </div>
      </div>

      <!-- Content -->
      <div class="__development-banner-content-wrapper">
        <div class="container">
          <!-- Title -->
          <h1 class="__development-banner-title">
            {{ companyName }}
          </h1>

          <!-- Development Meta Info -->
          <div class="__development-banner-info-action-bar">
            <div class="__development-banner-info-bar">
              <div class="__development-banner-development-type">
                {{ company.profileType }}
              </div>
              <div class="__development-banner-development-views">
                <img v-lazy-load  :data-src="eyeIcon" alt="View icon">
                Views: <BlocksLoadingDots v-if="viewsCount === null" /> <span v-else style="margin-left: 2px">{{ numberFormat(viewsCount) }}</span>
              </div>
            </div>
            <div class="__action-bar-and-video-button-wrapper">
              <!-- Video and 360 section -->
              <div v-if="company.video360Url || company.videoUrl" class="__video-button-wrapper">
                <ElementsButtonsIcon v-if="company.videoUrl" :icon-src="videoIcon" label="Video" @click="playVideo('video')" />
                <div v-if="company.videoUrl && company.video360Url" class="__separator" />
                <ElementsButtonsIcon v-if="company.video360Url" :icon-src="video360Icon" label="360 Tour" @click="playVideo('360')" />
              </div>

              <!-- Action bar -->
              <div class="__action-bar">
                <!-- Favorite button -->
                <div class="__favorite-button-wrapper" :class="{'--is-favorite': isAFavorite}">
                  <ElementsAvatar :icon-src="isAFavorite ? favoriteSelectedIcon : favoriteIcon" icon-alt="Favorite icon" @click="toggleAddToFavorite();" />
                </div>
                <CommonSocialSharePlatforms :url="shareUrl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Gallery Popup -->
    <BlocksGalleryFullScreen v-if="Array.isArray(company.gallery.data) && company.gallery.data.length > 0" v-model="isGalleryOpen" :selected-image-index="selectedImageIndex" :images="galleryMedias" />
  </section>
</template>

<script setup lang="ts">
import eyeIcon from '~/assets/images/icons/eye.webp';
import favoriteIcon from '~/assets/images/icons/favorite.webp';
import chevronDownIcon from '~/assets/images/icons/chevron-down.webp';
import galleryIcon from '~/assets/images/icons/gallery.webp';
import {computed, ComputedRef, PropType, Ref} from 'vue';
import {Company} from '~/@types/company';
import favoriteSelectedIcon from 'assets/images/icons/favorite-selected.webp';
import {useAuthUserStore} from '~/store/authUser';
import numberFormat from '~/composables/numberFormat';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {ImageItem} from '~/@types/common';
import videoIcon from 'assets/images/icons/video.webp';
import video360Icon from 'assets/images/icons/360-video.webp';

const props = defineProps({
  company: {
    type: Object as PropType<Company>,
    required: true,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isGalleryOpen: Ref<boolean> = ref(false);
const router = useRouter();
const shareUrl: Ref<string> = ref('');
const {$metaPixelTrackEvent} = useNuxtApp();
const selectedImageIndex: Ref<number> = ref(0);
const parallaxTopPosition: Ref<number> = ref(60);
const companyName: ComputedRef<string> = computed(() => props.company.displayName || props.company.name);
const authUserStore = useAuthUserStore();
const viewsCount: Ref<number | null> = ref(null);
const config = useRuntimeConfig();

onMounted(() => {
  shareUrl.value = window && window.location ? window.location.href : '';

  // Get view count
  getViewsCount();
});

/**
 * Get gallery medias
 */
const galleryMedias: ComputedRef<ImageItem[]> = computed(() => {
  if (props.company) {
    const returnArray = props.company.gallery.data.map((image) => ({
      url: image.url,
      type: 'image',
      caption: image.caption || '',
    } as ImageItem));

    const findHeroImage = props.company?.gallery.data.find((image) => image.url === props.company?.bannerUrl);
    if (!findHeroImage && props.company?.bannerUrl) {
      returnArray.unshift({
        url: props.company.bannerUrl,
        type: 'image',
        caption: '',
      });
    }

    // Array to hold videos to be inserted
    const videosToInsert = [];

    if (props.company.videoUrl) {
      videosToInsert.push({
        url: props.company.videoUrl,
        type: 'video',
        caption: `${props.company.name} video`,
      });
    }

    // Insert videos at the second and third positions
    returnArray.splice(1, 0, ...videosToInsert);

    return returnArray;
  }
  return [];
});


/**
 * Play the video
 *
 * @param type
 */
function playVideo (type: 'video' | '360') {
  if (type === 'video' && props.company?.videoUrl) {
    selectedImageIndex.value = 2;
    isGalleryOpen.value = true;
  } else if (props.company?.video360Url) {
    selectedImageIndex.value = props.company.videoUrl ? 3 : 2;
    isGalleryOpen.value = true;
  }
}

/**
 * Add/Remove from favorite
 */
function toggleAddToFavorite () {
  const favorites = authUserStore.favorites;
  const did = props.company.did;
  if (favorites && favorites.length > 0) {
    const index = favorites.findIndex((item) => item.did === did);
    if (index > -1) {
      authUserStore.setFavorites(favorites.filter((item) => item.did !== did));
    } else {
      authUserStore.setFavorites([...favorites, {
        type: 'development',
        did: did,
      }]);

      // Add to favorites Pixel event
      $metaPixelTrackEvent('AddToWishlist', {
        content_name: props.company.name,
        content_category: props.company?.profileType ?? 'Builder',
        content_ids: [props.company.did],
        content_type: 'Company',
      });
    }
  } else {
    authUserStore.setFavorites([{
      type: 'development',
      did: did,
    }]);
  }
}

/**
 * Check whether this listing is a favorite or not
 */
const isAFavorite: ComputedRef<boolean> = computed(() => {
  const favorites = authUserStore.favorites;
  const did = props.company.did;
  if (favorites && favorites.length > 0) {
    const index = favorites.findIndex((item) => item.did === did);
    return index > -1;
  }
  return false;
});

function goBack () {
  router.back();
}

/**
 * Get views count
 */
function getViewsCount () {
  setTimeout(() => {
    if (props.company?.slug) {
      $fetch(`/public/companies/view-count/${props.company.slug}`,
        { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
      ).then((response) => {
        viewsCount.value = response as number;
      });
    }
  }, 1500);
}
</script>

<style lang="scss">
.sections-development-banner {
  height: 420px;
  padding: 0 !important;

  .__development-banner-wrapper {
    position: relative;
    height: 100%;

    .__development-banner-image-wrapper {
      display: flex;
      cursor: pointer;
      //position: fixed;
      width: 100%;
      height: 420px;
      z-index: 0;

      .__column {
        height: 100%;
        width: 100%;
      }

      .__development-banner-primary-image {
        object-fit: cover;
      }
    }

    .__development-banner-go-back-button-wrapper {
      position: absolute;
      top: 20px;
      width: 100%;
      height: fit-content;

      .container {
        display: flex;
        justify-content: space-between;
      }

      .__development-banner-go-back-btn img {
        transform: rotate(90deg);
      }
    }

    .__development-banner-content-wrapper {
      padding: 120px 0 0;
      position: absolute;
      bottom: 0;
      width: 100%;
      height: fit-content;
      color: rgb(var(--text-light));
      z-index: 1;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #272655 100%);

      .__development-banner-title {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);
        font-size: 36px;
        font-weight: 500;
      }

      .__development-banner-info-action-bar {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        justify-content: space-between;
        padding: 8px 0 5px;

        .__development-banner-info-bar {
          display: flex;
          align-items: center;
          font-weight: 300;
          font-size: 14px;
          margin-right: 20px;

          &>:not(:last-child) {
            margin-right: 30px;
          }

          .__development-banner-development-type {
            text-transform: uppercase;
            font-size: 15px;
          }

          &>* {
            display: flex;
            align-items: center;
          }

          .__development-banner-development-views {
            img {
              width: 18px;
              height: auto;
              margin-right: 8px;
            }
          }

          .__development-added-date {
            img {
              width: 14px;
              height: auto;
              margin-right: 8px;
            }
          }
        }
        .__action-bar-and-video-button-wrapper {
          display: flex;
        }

        .__video-button-wrapper {
          margin-right: 24px;
          display: flex;
          padding: 4px 20px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          .__separator {
            width: 1px;
            height: 100%;
            background: #b2b2b2;
            margin-left: 12px;
            margin-right: 12px;
          }

          .el-icon-button {
            width: 80px;
            display: flex;
            justify-content: center;

            .__icon-button-label {
              color: #fff;
            }

            .__el-icon-button-inner {
              border-radius: 50%;
              padding: 8px;
              background: #fff;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            &:hover {
              background: unset !important;
            }
          }

          @media screen and (max-width: $grid-md) {
            display: none;
          }
        }

        .__action-bar {
          display: flex;
          padding: 4px 5px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          :not(:first-child) {
            border-left: 1px solid rgba(var(--text-light), 0.5);
          }

          .el-avatar {
            padding: 0 12px;
            align-items: center;
            display: flex;
          }

          .__favorite-button-wrapper {
            margin-right: 8px;
            cursor: pointer;
            transition: all 0.4s ease-in-out !important;

            .el-avatar .__el-avatar-inner {
              scale: 1;
              transition: all 0.2s ease-in-out !important;
            }

            &.--is-favorite {
              .el-avatar {
                .__el-avatar-inner {
                  -webkit-box-shadow: inset 0 0 8px 0 rgba(0,0,0,0.4) !important;
                  box-shadow: inset 0 0 5px 0 rgba(0,0,0,0.4) !important;
                  transition: all 0.4s ease-in-out !important;
                }
              }
            }

            &:hover {
              .el-avatar {
                .__el-avatar-inner {
                  scale: 1.1;
                  transition: all 0.2s ease-in-out !important;
                }
              }
            }
          }
        }
      }

      // Responsive
      @media screen and (max-width: $grid-lg) {
        .__development-banner-title {
          text-align: center;
          width: 100%;
          margin-bottom: 8px;
        }

        .__development-banner-info-action-bar {
          justify-content: center;
        }
      }

      @media screen and (max-width: $grid-sm) {
        .__development-banner-title {
          font-size: 28px;
        }

        .__development-banner-info-action-bar {
          padding-bottom: 0;

          .__development-banner-info-bar {
            font-size: 12px;

            &>:not(:last-child) {
              margin-right: 15px;
            }

            .__development-banner-development-type {
              font-size: 14px;
            }
          }

          .__action-bar {
            padding: 2px 4px;
            border-radius: 20px;
            margin-right: 0;

            .el-icon-button {

              &:not(:last-child) {
                margin-right: 12px;
              }

              .__el-icon-button-inner {
                width: 20px !important;
                padding: 4px !important;

                img {
                  width: 100%;
                  height: auto;
                }
              }

              .__label {
                font-size: 12px !important;
              }
            }

            .el-avatar {
              padding: 0 5px;

              .__el-avatar-inner {
                width: 25px;
                height: 25px;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: $grid-md) {
    height: 420px;

    .__development-banner-wrapper .__development-banner-image-wrapper {
      flex-direction: column;
    }
  }
}
</style>
